import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import AuthContext from "../../../contexts/AuthContext";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import ContentCreator from "../../../components/ContentCreator/ContentCreator";
import showNotification from "../../../utils/showNotification";
import { currencyFormat } from "../../../utils/currencyFormat";
import AcademicPackForm from "./AcademicPackForm";
import AcademicPackPreview from "./AcademicPackPreview";

const LEGACY_PACK_REGEX = /^\s*pack\b/i;

const DEFAULT_WHO_IS_THIS_FOR = "Nuestros cursos de especialización están dirigidos exclusivamente a profesionales graduados en Medicina Veterinaria. Requerimos la titulación en esta disciplina para garantizar una base sólida de conocimientos previos y maximizar el aprendizaje en áreas especializadas.";

const EMPTY_PACK = {
    academicUnits: [],
    state: "Open",
    division: "SMALL",
    studentsCount: 0,
    comercialInformation: {
        name: "",
        description: "",
        whoIsThisFor: DEFAULT_WHO_IS_THIS_FOR,
        image: "",
        video: "",
        is_highlight: false,
        saleStartDateISO: null,
        saleEndDateISO: null,
        price: { usd: 0, ars: 0 },
    },
};

const validation = {
    academicUnits: (value) => (Array.isArray(value) && value.length >= 2 ? null : "Elegí al menos 2 unidades académicas"),
    comercialInformation: {
        name: (value) => (value?.trim() ? null : "El nombre es obligatorio"),
        description: (value) => (value?.trim() ? null : "La descripción es obligatoria"),
        price: {
            usd: (value) => (Number(value) > 0 ? null : "Ingresá el precio en USD"),
            ars: (value) => (Number(value) > 0 ? null : "Ingresá el precio en ARS"),
        },
    },
};

// Body para POST/PUT /admin/academico/packs. Los packs no tienen planes de cuotas.
const buildPackPayload = (values) => {
    const { name, description, whoIsThisFor, image, video, is_highlight, saleStartDateISO, saleEndDateISO, price } = values.comercialInformation;
    return {
        data: {
            academicUnits: values.academicUnits,
            state: values.state,
            division: values.division,
            comercialInformation: { name, description, whoIsThisFor, image, video, is_highlight, saleStartDateISO, saleEndDateISO, price },
        },
    };
};

const toFormValues = (pack) => ({
    _id: pack._id,
    academicUnits: (pack.academicUnits || []).map(au => (typeof au === "object" ? au._id : au)),
    state: pack.state || "Open",
    division: pack.division || "SMALL",
    studentsCount: pack.studentsCount || 0,
    comercialInformation: {
        ...EMPTY_PACK.comercialInformation,
        ...pack.comercialInformation,
        price: { ...EMPTY_PACK.comercialInformation.price, ...(pack.comercialInformation?.price || {}) },
        whoIsThisFor: pack.comercialInformation?.whoIsThisFor || DEFAULT_WHO_IS_THIS_FOR,
    },
});

function AcademicPacksContainer() {
    const { authToken } = useContext(AuthContext);
    const [packs, setPacks] = useState(null);
    const [academicUnits, setAcademicUnits] = useState([]);
    const [displayForm, setDisplayForm] = useState(false);
    const [initialValues, setInitialValues] = useState(null);
    const [loading, setLoading] = useState(false);
    const [updateData, setUpdateData] = useState(false);

    const config = useMemo(() => ({
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
    }), [authToken]);

    useEffect(() => {
        const init = async () => {
            try {
                const [packsResponse, academicUnitsResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_SERVER}admin/academico/packs`, config),
                    academicUnits.length ? Promise.resolve(null) : axios.get(`${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/acaUnits`, config),
                ]);
                setPacks(packsResponse.data);
                if (academicUnitsResponse) setAcademicUnits(academicUnitsResponse.data || []);
            } catch (error) {
                showNotification({
                    color: "red",
                    status: "error",
                    title: "No se pudieron cargar los packs.",
                    message: `Detalle: ${error?.response?.data?.message || error.message}`,
                });
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateData, config]);

    const academicUnitsById = useMemo(
        () => Object.fromEntries(academicUnits.map(au => [au._id, au])),
        [academicUnits]
    );

    // Las UAs "PACK ..." viejas no se ofrecen: un pack no puede contener otro pack
    const academicUnitOptions = useMemo(() => academicUnits
        .filter(au => !LEGACY_PACK_REGEX.test(au.comercialInformation?.name || ""))
        .sort((a, b) => new Date(b.comercialInformation?.initialDateISO || 0) - new Date(a.comercialInformation?.initialDateISO || 0))
        .map(au => {
            const date = au.comercialInformation?.initialDateISO;
            return {
                value: au._id,
                label: `${au.comercialInformation?.name}${date ? ` (${moment.utc(date).format("MM/YYYY")})` : ""}`,
            };
        }), [academicUnits]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const payload = buildPackPayload(values);
            values._id
                ? await axios.put(`${process.env.REACT_APP_BACKEND_SERVER}admin/academico/packs/${values._id}`, payload, config)
                : await axios.post(`${process.env.REACT_APP_BACKEND_SERVER}admin/academico/packs`, payload, config);

            showNotification({
                color: "green",
                status: "success",
                title: values._id ? "Pack actualizado." : "Pack creado.",
                message: `${values.comercialInformation.name} se guardó correctamente.`,
            });
            setDisplayForm(false);
            setInitialValues(null);
            setUpdateData(prev => !prev);
        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "No se pudo guardar el pack.",
                message: `Detalle: ${error?.response?.data?.message || error.message}`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
        }
    };

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor("comercialInformation.name", {
            header: "Nombre",
            size: 304,
            cell: (info) => info.getValue(),
            enableColumnFilter: true,
            enableSorting: false,
            filter: "text",
            filterFn: (row, id, filterValue) => !filterValue
                || (row.original.comercialInformation?.name || "").toLowerCase().includes(filterValue.toLowerCase()),
        }),
        columnHelper.accessor("academicUnits", {
            header: "Cursos",
            size: 88,
            cell: (info) => <span title={(info.getValue() || []).map(au => au?.comercialInformation?.name).join("\n")}>{(info.getValue() || []).length}</span>,
            enableColumnFilter: false,
            enableSorting: false,
        }),
        columnHelper.accessor("comercialInformation.price.usd", {
            header: "USD",
            size: 104,
            cell: (info) => `$${currencyFormat(info.getValue() || 0)}`,
            enableColumnFilter: false,
            enableSorting: true,
        }),
        columnHelper.accessor("comercialInformation.price.ars", {
            header: "ARS",
            size: 104,
            cell: (info) => `$${currencyFormat(info.getValue() || 0)}`,
            enableColumnFilter: false,
            enableSorting: true,
        }),
        columnHelper.accessor("studentsCount", {
            header: "Compradores",
            size: 120,
            cell: (info) => info.getValue() || 0,
            enableColumnFilter: false,
            enableSorting: true,
        }),
        columnHelper.accessor("state", {
            header: "Estado",
            size: 104,
            cell: (info) => (info.getValue() === "Open" ? "Abierto" : "Cerrado"),
            enableColumnFilter: true,
            enableSorting: false,
            filter: "select",
            filterOptions: [
                { value: "Open", label: "Abierto" },
                { value: "Closed", label: "Cerrado" },
            ],
            filterFn: (row, id, filterValue) => !filterValue || row.original.state === filterValue,
        }),
    ];

    return (
        <div className="acaUnitPrice__container">
            {displayForm && (
                <ContentCreator
                    title={initialValues?._id ? "Editar pack" : "Crear pack"}
                    validation={validation}
                    initialValues={initialValues || EMPTY_PACK}
                    submitFunc={handleSubmit}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialValues(null);
                    }}
                    renderPreview={({ form }) => (
                        <AcademicPackPreview values={form.values} academicUnitsById={academicUnitsById} />
                    )}
                    renderForm={({ form }) => (
                        <AcademicPackForm form={form} loading={loading} academicUnitOptions={academicUnitOptions} />
                    )}
                />
            )}
            {packs && (
                <TanStackTable
                    data={packs}
                    columns={columns}
                    loading={loading}
                    displayForm={displayForm}
                    editFunc={(rowData) => {
                        setInitialValues(toFormValues(rowData));
                        setDisplayForm(true);
                    }}
                    addButtonText="Crear pack"
                    addButtonFunc={() => {
                        if (displayForm) {
                            setDisplayForm(false);
                            setInitialValues(null);
                        } else {
                            setInitialValues(EMPTY_PACK);
                            setDisplayForm(true);
                        }
                    }}
                />
            )}
        </div>
    );
}

export default AcademicPacksContainer;
